/**
 * Browser plumbing for the call center: the Twilio SDK (lazy-loaded so the
 * ~300 KB SDK only reaches staff who take calls), a timer that keeps running
 * in a hidden tab, and the one-tab-per-browser leader lock.
 *
 * The SDK is reached only through the narrow interfaces below, so the
 * provider and its tests depend on what we use, not on the SDK's full types.
 */

export interface VoiceCall {
  readonly customParameters: Map<string, string>;
  readonly parameters?: Record<string, string>;
  accept(): void;
  reject(): void;
  ignore?(): void;
  disconnect(): void;
  mute(shouldMute?: boolean): void;
  isMuted(): boolean;
  on(event: string, listener: (...args: any[]) => void): unknown;
}

export interface VoiceDevice {
  register(): Promise<void>;
  unregister(): Promise<void>;
  updateToken(token: string): void;
  destroy(): void;
  on(event: string, listener: (...args: any[]) => void): unknown;
}

export type VoiceDeviceFactory = (token: string) => Promise<VoiceDevice>;

/** Create a Twilio Device. The SDK is imported on first use. */
export const createTwilioDevice: VoiceDeviceFactory = async (token) => {
  const sdk = await import("@twilio/voice-sdk");
  const device = new sdk.Device(token, {
    // A reload or tab close during a live call asks first.
    closeProtection: "You are on a call. Leaving this page will hang it up.",
    // A second call while on one is turned away rather than ringing over it.
    allowIncomingWhileBusy: false,
    // Refresh well before the hour-long token runs out.
    tokenRefreshMs: 60_000,
  });
  return device as unknown as VoiceDevice;
};

// ---------------------------------------------------------------------------
// Worker timer
// ---------------------------------------------------------------------------

/**
 * Call `fn` every `ms`. Chrome throttles a hidden tab's own timers to about
 * once a minute; a Web Worker's timers are not throttled the same way, so
 * the heartbeat is ticked from one. Falls back to setInterval where Workers
 * or blob URLs are unavailable (tests, locked-down browsers).
 *
 * Returns a stop function.
 */
export function startWorkerInterval(fn: () => void, ms: number): () => void {
  const canWorker =
    typeof window !== "undefined" &&
    typeof (window as any).Worker === "function" &&
    typeof URL !== "undefined" &&
    typeof URL.createObjectURL === "function" &&
    typeof Blob !== "undefined";
  if (canWorker) {
    try {
      const source = `let t=null;onmessage=function(e){if(e.data&&e.data.start){clearInterval(t);t=setInterval(function(){postMessage(1)},e.data.start)}else{clearInterval(t)}}`;
      const url = URL.createObjectURL(new Blob([source], { type: "application/javascript" }));
      const worker = new Worker(url);
      worker.onmessage = () => fn();
      worker.postMessage({ start: ms });
      return () => {
        try {
          worker.postMessage({ stop: true });
          worker.terminate();
        } finally {
          URL.revokeObjectURL(url);
        }
      };
    } catch {
      // fall through to a plain interval
    }
  }
  const id = window.setInterval(fn, ms);
  return () => window.clearInterval(id);
}

// ---------------------------------------------------------------------------
// Leader tab
// ---------------------------------------------------------------------------

export const LEADER_LOCK_NAME = "cc-voice-device";

/**
 * Hold the browser-wide lock while this tab is the one that rings. Only one
 * tab registers a Twilio Device, so a call never rings in five tabs at once.
 * `onLeader` runs when this tab gets the lock (immediately when it is free,
 * or later when the leading tab closes). Without the Web Locks API every tab
 * leads, which is what a browser without it did before.
 *
 * Returns a release function.
 */
export function acquireLeaderLock(onLeader: () => void): () => void {
  let released = false;
  let release: () => void = () => {
    released = true;
  };
  const locks = typeof navigator !== "undefined" ? (navigator as any).locks : undefined;
  if (!locks || typeof locks.request !== "function") {
    onLeader();
    return () => {
      released = true;
    };
  }
  const held = new Promise<void>((resolve) => {
    release = () => {
      released = true;
      resolve();
    };
  });
  try {
    Promise.resolve(
      locks.request(LEADER_LOCK_NAME, () => {
        if (released) return undefined;
        onLeader();
        return held;
      })
    ).catch(() => false);
  } catch {
    onLeader();
  }
  return () => release();
}
